import { useContext, useRef } from 'react';
import axios, { AxiosProgressEvent, Cancel } from 'axios';
import Cookies from 'js-cookie';
import { ConsoleLog } from '@inntechcorp/it-design';

import BackOfficeContext from 'Context/BackOfficeContext';

import { useNavigate } from 'react-router';
import { GetLocalState } from 'Connectivity/LocalStorage';
import i18next from 'i18next';
import { APIErrorDetails, APIPredefinedCodeValues, APIPredefinedFieldValues, ErrorTypes } from 'Types/enums';
import { toast } from 'sonner';

export type INNError = {
  detail  :string,
  source  :string,
  type    :string
}

/**
 * 
 * usePostFetch
 * --
 * 
 * All API routes within the application pass through this class. It filters all 
 * responses returned from the API and processes them to their expected state.
 * Then, it returns the success or failure status.
 * 
 * Additionally, it retrieves and adds additional information such as language,
 * token, currency, etc., from the user's cookie to API calls.
 * 
 * @param changeLogout      The function used to log out the user when called from places where 
 *                          context data such as React.Portal cannot be accessed needs to be passed.
 * @param isUserLoggedOut   User's logout information.
 * 
 * @returns 
 * 
 */
export const usePostFetch = () => {

  const axiosSource: any                = useRef(null);
  const navigate                        = useNavigate();
  const { ChangeLogout, isLoggingOut, isLoggedIn }  = useContext(BackOfficeContext);

  const language = Cookies.get("aff-language");
  const currency = Cookies.get("aff-currency");

  const translateCell = (cell:string) => {
    return (i18next.t(cell));
  }

  const logout = (status:string, message?:string) => {
    console.log("logout", status, message);
    /**
     * Wherever it can access the context information, it first invokes the function
     * to log the user out directly using the context data.
     */
    if ( ChangeLogout && !isLoggingOut ) {
      ChangeLogout(status, message);
    }
    else {

      let path = GetLocalState("settings").paths.SignIn;

      if ( location.pathname !== GetLocalState("settings").paths.SignIn ) {
        path += "?return=" + location.pathname;
      }

      navigate(path);

    }

  }

  const addErrorToConsole = (source = "", message = "") => {
    
    ConsoleLog.double("🔴 Critical API Error [usePostFetch]", source + " - " + i18next.t(message), "error");
    
    if ( !isLoggingOut && isLoggedIn ) {
      showNotification({
        title   : "forms.notifications.error",
        message : source + " - " + i18next.t(message)
      })
    }

  };

  const showNotification = (props:{title?:string, message?: string, type?: string} ) => {

    const title   = translateCell(props.title ? props.title : "forms.notifications.error");
    const message = translateCell(props.message ? props.message : "");
    
    if ( props.type === "error" ) {
      toast.error(
        title,
        {
          description : message,
        }
      );
    }
    else if ( props.type === "warning" ) {
      toast.warning(
        title,
        {
          description : message,
        }
      );
    }
    else if ( props.type === "success" ) {
      toast.success(
          title,
        {
          description : message,
        }
      );
    }

  }

  /**
   * Used to check if error returned in response is a cancel token error.
   * 
   * When a component unmounts, we need to cancel any potentially
   * ongoing Axios calls that result in a state update on success / fail.
   * This function sets up the appropriate useEffect to handle the cancelling.
   * 
   */
  const cancel = () => {
    if (axiosSource.current) {
      axiosSource.current.cancel("manually canceled!");
    }
  }

  /**
   * Used to generate the cancel token sent in the Axios request.
   */
  const createNewToken = () => {

    axiosSource.current = axios.CancelToken.source();
    return axiosSource.current.token;

  };

  const fetch = async (
    type    = "post",
    _url    = "",
    _params = {}, 
    { 
      cancelable  = true, 
      onFetched   = (data:{}) => {data}, 
      onError     = (error?:INNError) => {error}, 
      onCanceled  = (message:Cancel) => {message},
      onProgress  = (event:AxiosProgressEvent) => {event}
    }, 
    _queries:any = {},
    _headers:any = {}
  ) => {

    let queries = "?lang=" + ((language === undefined)      ? "not_found" : language);
        queries += "&currency=" + ((currency === undefined) ? "not_found" : currency);

    Object.keys(_queries).forEach((property) => {
      queries += "&" + property + "=" + _queries[property];
    });

    const headers:any = {
      'Content-Type': type === "put" ? 'multipart/form-data' : 'application/json',
      'Access-Control-Allow-Headers': 'Origin, X-Requested-With, Content-Type, Accept',
      ..._headers
    }

    if ( Cookies.get("aff-token") !== "" && Cookies.get("aff-token") !== undefined ) {
      headers['x-auth-token'] = Cookies.get("aff-token");
    }

    let fetchType:any = axios.post;

    if ( type === "post" )  fetchType = axios.post;
    if ( type === "get" )   fetchType = axios.get;
    if ( type === "put" )   fetchType = axios.put;
    if ( type === "patch" ) fetchType = axios.patch;

    await fetchType(
        _url + queries, 
        _params, 
        {
          headers: headers,
          cancelToken: cancelable ? createNewToken() : false,
          onUploadProgress:(progressEvent:AxiosProgressEvent) => {
            onProgress(progressEvent);
          },
        },
      )
      .then((response:any) => {

        if ( response.hasOwnProperty("data") ) {

          /**
           * /////////////////////////
           * //////// WARNING ////////
           * /////////////////////////
           */
          if ( response.data.hasOwnProperty("warning") ) {

            /**
             * PASSWORD EXPIRED
             */
            if (
              response.data.warning.field === APIPredefinedFieldValues.PASSWORD
              &&
              response.data.warning.code === APIPredefinedCodeValues.EXPIRED
            ) {
              
              onFetched(response.data);
              showNotification({message: "notifications.pleaseChangePasswordFirst", type: "warning"});
              navigate(GetLocalState("settings").paths.ForceChangePassword);
              
              return;
            }
            /**
             * TOKEN EXPIRED
             */
            else if (
              response.data.warning.field === APIPredefinedFieldValues.TOKEN
              &&
              response.data.warning.code === APIPredefinedCodeValues.EXPIRED
            ) {
              logout("tokenIsExpired", "forms.notifications.tokenIsExpired");
              if ( onError ) onError();
              return;
            }
            /**
             * TOKEN MISSING
             */
            else if (
              response.data.warning.field === APIPredefinedFieldValues.TOKEN
              &&
              response.data.warning.code === APIPredefinedCodeValues.REQUIRED
            ) {
              logout("tokenIsMissing", "forms.notifications.tokenIsMissing");
              if ( onError ) onError();
              return;
            }

          }
          /**
           * /////////////////////////
           * //////// WARNING ////////
           * /////////////////////////
           */
          else if ( response.data.hasOwnProperty("warnings") ) {

            if ( response.data.warnings[0].code === "tokenIsMissing" ) {
              if ( onError ) {
                onError();
              }
              else {
                logout("tokenIsMissing", "forms.notifications.tokenIsMissing");
              }
              return;
            }
            else if ( response.data.warnings[0].code === "adminBeApiTokenLimitExceeded" ) {
              if ( onError ) {
                onError();
              }
              else {
                addErrorToConsole(
                  "adminBeApiTokenLimitExceeded",
                  "forms.notifications.adminBeApiTokenLimitExceeded"
                );
              }
              return;
            }
            else if ( response.data.warnings[0].code === "clientOnlyEndpoint" ) {
              if ( onError ) {
                onError();
              }
              else {
                addErrorToConsole("clientOnlyEndpoint", "forms.notifications.clientOnlyEndpoint");
              }
              return;
            }

          }
          /**
           * ///////////////////////
           * //////// ERROR ////////
           * ///////////////////////
           */
          else if ( response.data.hasOwnProperty("error") ) {

            /**
             * THIS RUNS IF AN INVALID ID IS SENT TO THE API.
             */
            if ( response.data.error.detail === APIErrorDetails.PROVIDED_HEX_STRING_IS_NOT_A_VALID_OBJECT_ID ) {
              showNotification({title: "forms.notifications.error", message: "forms.notifications.providedHexStringIsNotAValidObjectId", type: "error"});
            }

            /**
             * IF AN ERROR HANDLER IS PROVIDED TO THIS CLASS, ERRORS ARE PASSED TO THE HANDLER.
             */
            if ( onError ) {
              onError(response.data.error);
            }
            /**
             * IF AN ERROR HANDLER IS NOT PROVIDED TO THIS CLASS, ERRORS ARE LOGGED TO THE CONSOLE.
             */
            else {
              addErrorToConsole(response.data.error.source, response.data.error.detail);
            }
            
            return;

          }

        }
        
        /**
         * /////////////////////////
         * //////// SUCCESS ////////
         * /////////////////////////
         */
        onFetched(response.data);

      })
      /**
       * /////////////////////////////////
       * //////// UNHANDLED ERROR ////////
       * /////////////////////////////////
       */
      .catch((error:any) => {

        let message = "";

        if ( error.hasOwnProperty("data") ) {
          if ( error.response.hasOwnProperty("response") ) {
            message = error.response.data.error.message;
          }
          else {
            message = error.response.statusText;
          }
        }

        if (axios.isCancel(error)) {
          onCanceled(error);
        }
        else {
          
          if (error.response) {

            let code = 0;
            let type = "";

            // BAD REQUEST
            if (error.response.status === 400) {
              ConsoleLog.double("🔴 Critical API Error [usePostFetch] 400 Bad Request", error, "error");
              type  = ErrorTypes.BAD_REQUEST;
              code  = 400;
            }
            // FORBIDDEN
            else if (error.response.status === 403) {
              ConsoleLog.double("🔴 Critical API Error [usePostFetch] 403 Forbidden", error, "error");
              type  = ErrorTypes.FORBIDDEN;
              code  = 403;
            }
            // UNAUTHORIZED USER
            else if (error.response.status === 401) {
              ConsoleLog.double("🔴 Critical API Error [usePostFetch] 401 Unauthorized User", error, "error");
              type  = ErrorTypes.UNAUTHORIZED_USER;
              code  = 401;
            }
            // RESOURCE NOT FOUND
            else if (error.response.status === 404) {
              ConsoleLog.double("🔴 Critical API Error [usePostFetch] 404 Resource Not Found", error + " - " + _url, "error");
              type  = ErrorTypes.RESOURCE_NOT_FOUND;
              code  = 404;
            } 
            // TIMEOUT
            else if (error.response.status === 408) {
              ConsoleLog.double("🔴 Critical API Error [usePostFetch] 408 Timeout", error, "error");
              type  = ErrorTypes.TIMEOUT;
              code  = 408;
            } 
            // REQUEST ENTITY TOO LARGE
            else if (error.response.status === 413) {
              ConsoleLog.double("🔴 Critical API Error [usePostFetch] 413 Request Entity Too Large", error, "error");
              type  = ErrorTypes.REQUEST_ENTITY_TOO_LARGE;
              code  = 413;
            } 
            // INTERNAL SERVER ERROR
            else if (error.response.status === 500) {
              ConsoleLog.double("🔴 Critical API Error [usePostFetch] 500 Internal Server Error", error, "error");
              type  = ErrorTypes.INTERNAL_SERVER_ERROR;
              code  = 500;
              logout("internalServerError");
            } 
            // BAD GATEWAY
            else if (error.response.status === 502) {
              ConsoleLog.double("🔴 Critical API Error [usePostFetch] 502 Bad Gateway", error, "error");
              type  = ErrorTypes.BAD_GATEWAY;
              code  = 502;
              logout("badGateway");
            } 
            // SERVICE TEMPORARILY UNAVAILABLE
            else if (error.response.status === 503) {
              ConsoleLog.double("🔴 Critical API Error [usePostFetch] 503 Service Temporarily Unavailable", error, "error");
              type  = ErrorTypes.SERVICE_TEMPORARILY_UNAVAILABLE;
              code  = 503;
              logout("serviceTemporarilyUnavailable");
            } 
            // PROXY ERROR
            else if (error.response.status === 504) {
              ConsoleLog.double("🔴 Critical API Error [usePostFetch] 504 Proxy Error", error, "error");
              type  = ErrorTypes.PROXY_ERROR;
              code  = 504;
            }

            if ( onError ) {
              onError({
                source  : code > 0 ? String(code) : error.status,
                type    : type ? type : error.name,
                detail  : message ? message : error.message
              });
            }
            else {
              ConsoleLog.double("🔴 Critical API Error [usePostFetch]", error, "error");
            }

          } 
          else if (error.request) {

            logout("noResponseFromApiPath");

            if ( onError ) {
              onError({
                source  : "SERVICE",
                type    : ErrorTypes.NO_RESPONSE,
                detail  : "No Response from api path: " + _url
              });
            }

            // The request was made but no response was received
            // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
            // http.ClientRequest in node.js

          } 
          else {

            // Something happened in setting up the request that triggered an Error
            //console.log(error);
            
            if ( onError ) {
              onError(error);
            }
            else {
              logout("unhandledError");
            }

          }

        }
      });

  }

  return { fetch, cancel };

};